import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react'
import styled from 'styled-components'

const NotesContainer = styled.div`
  width: 100%;
  max-width: 100%;
`

const StyledTextarea = styled.textarea<{ $isFocused: boolean }>`
  min-height: 44px;
  padding: 8px 12px;
  font-size: 0.75rem;
  font-family: inherit;
  line-height: 1.4;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  outline: none;
  width: 100%;
  background: ${(props) =>
    props.$isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  transition: background-color 0.2s ease;
  resize: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    border-color: rgba(255, 255, 255, 0.6);
  }
`

export interface MarkupNotesInputRef {
  focus: () => void
}

export interface MarkupNotesInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  onEnter?: () => void
  onTabKey?: (e: React.KeyboardEvent) => void
  onEscape?: () => void
  saveOnBlur?: boolean
}

export const MarkupNotesInput = forwardRef<
  MarkupNotesInputRef,
  MarkupNotesInputProps
>(
  (
    {
      value,
      onChange,
      placeholder = 'Add notes...',
      disabled = false,
      onEnter,
      onTabKey,
      onEscape,
      saveOnBlur = false,
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const textareaRef = useRef<HTMLTextAreaElement>(null)

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }))

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (onEscape) {
          onEscape()
        }
        return
      }

      e.stopPropagation()

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (onEnter) {
          onEnter()
        }
        textareaRef.current?.blur()
      } else if (e.key === 'Tab') {
        e.preventDefault()
        onTabKey?.(e)
      }
    }

    const handleBlur = () => {
      setIsFocused(false)
      if (saveOnBlur && onEnter) {
        onEnter()
      }
    }

    return (
      <NotesContainer>
        <StyledTextarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          onKeyUp={(e) => e.stopPropagation()}
          placeholder={placeholder}
          $isFocused={isFocused}
          disabled={disabled}
        />
      </NotesContainer>
    )
  }
)

MarkupNotesInput.displayName = 'MarkupNotesInput'
